import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NpcTalkSettings } from './npc/NpcTalkSettings';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { Lock, Unlock, RotateCcw, X, Trash2, Copy, Move, RotateCw, Maximize2, ShieldCheck, Upload, Loader, Package, BookOpen, Box, Armchair } from 'lucide-react';
import JSZip from 'jszip';
import { PrimitiveItem, PrimitiveShape, PbrMaps } from '../stores/worldStore';
import { useDraggable } from '../hooks/useDraggable';
import { useWorldStore } from '../stores/worldStore';
import { newPortalLink, portalPartner, startPlacePortalExit } from './portals/portals';
import { supabase } from '../config/supabase';
import { NPC_ANIMS, npcAnim, type NpcConfig } from '../config/npc';
import { useArcadeGames, artDataUrl, boardConfig, BOARD_THEMES, reframeBoardY } from './games/arcade/arcadeShared';
import type { LeaderboardConfig } from '../stores/worldStore';
import { watchNpcBuild } from '../services/npcBuild';

import { putObject } from '../utils/uploadToStorage';
import PBRBrowser from './PBRBrowser';
import './PrimitiveControls.css';

// Lazy: the music UI (library, uploads, transport) loads only for speakers.
const SpeakerSettings = React.lazy(() => import('./music/SpeakerSettings'));
const SpeakerSection: React.FC<{ primitive: PrimitiveItem; onUpdate: (u: Partial<PrimitiveItem>) => void; isLocked: boolean }> = (props) => {
  const id = useWorldStore((s) => s.selectedObjectId);
  if (!id) return null;
  return <React.Suspense fallback={null}><SpeakerSettings id={id} {...props} /></React.Suspense>;
};

import { WORLD_GAME_BOARDS } from './games/worldgame/worldGameShared';
// Lazy: the world-game piece settings load only when one is selected.
const WorldGameSettings = React.lazy(() => import('./games/worldgame/WorldGameSettings'));

// Module-level material clipboard — "Copy Material" on one object, "Apply" on
// another. Survives selection changes; cleared on page reload.
let materialClipboard: Partial<PrimitiveItem> | null = null;

const TEXTURE_PRESETS = [
  { id: 'water', label: 'Water' },
  { id: 'water2', label: 'Water 2' },
  { id: 'grass', label: 'Grass' },
  { id: 'dirt', label: 'Dirt' },
  { id: 'sand', label: 'Sand' },
  { id: 'wood1', label: 'Wood' },
  { id: 'wood2', label: 'Wood 2' },
  { id: 'wood3', label: 'Wood 3' },
  { id: 'wood4', label: 'Wood 4' },
  { id: 'tile1', label: 'Tile' },
  { id: 'tile2', label: 'Tile 2' },
  { id: 'tile3', label: 'Tile 3' },
  { id: 'marble', label: 'Marble' },
  { id: 'brick', label: 'Brick' },
  { id: 'cobblestone', label: 'Cobble' },
  { id: 'paving1', label: 'Paving' },
  { id: 'paving2', label: 'Paving 2' },
];

const SHAPE_LABELS: Record<PrimitiveShape, string> = {
  box: 'Box',
  sphere: 'Sphere',
  cylinder: 'Cylinder',
  cone: 'Cone',
  plane: 'Plane',
  torus: 'Torus',
  text: 'Text',
  portal: 'Portal',
  gametable: 'Game table',
  arcade: 'Arcade cabinet',
  leaderboard: 'Leaderboard',
  npc: 'NPC',
  speaker: 'Speaker',
  worldgame: 'World game',
};

interface PrimitiveControlsProps {
  primitive: PrimitiveItem;
  transformMode: 'translate' | 'rotate' | 'scale';
  onTransformModeChange: (mode: 'translate' | 'rotate' | 'scale') => void;
  onUpdate: (updates: Partial<PrimitiveItem>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onClose: () => void;
}

/** Worlds a portal can lead to: your own, then public ones. */
interface PortalWorld { id: string; name: string; mine: boolean }
async function loadPortalWorlds(exclude?: string): Promise<PortalWorld[]> {
  const { data: { user } } = await supabase.auth.getUser();
  const [mine, pub] = await Promise.all([
    user ? supabase.from('worlds').select('id, name').eq('owner_id', user.id).order('updated_at', { ascending: false }).limit(100) : Promise.resolve({ data: [] as { id: string; name: string }[] }),
    supabase.from('worlds').select('id, name').eq('settings->is_public', true).order('updated_at', { ascending: false }).limit(100),
  ]);
  const seen = new Set<string>(exclude ? [exclude] : []);
  const out: PortalWorld[] = [];
  for (const [rows, isMine] of [[mine.data || [], true], [pub.data || [], false]] as const) {
    for (const w of rows) if (!seen.has(w.id)) { seen.add(w.id); out.push({ id: w.id, name: w.name || 'Untitled world', mine: isMine }); }
  }
  return out;
}

/** Portal panel: a label, and where it leads (an exit in this world, or another world). */
function PortalSettings({ primitive, onUpdate, isLocked }: { primitive: PrimitiveItem; onUpdate: (u: Partial<PrimitiveItem>) => void; isLocked: boolean }) {
  const selected = useWorldStore((s) => s.selectedObjectId);
  const prims = useWorldStore((s) => s.primitives);
  const { worldId } = useParams<{ worldId: string }>();
  const partner = selected ? portalPartner(selected, prims) : null;
  const toWorld = !!primitive.portalWorldId;
  const [worlds, setWorlds] = useState<PortalWorld[] | null>(null);
  const [query, setQuery] = useState('');

  // portalWorldId '' = "Another world" picked but no world chosen yet.
  const pickingWorld = primitive.portalWorldId !== undefined;

  useEffect(() => {
    if (!pickingWorld || worlds) return;
    loadPortalWorlds(worldId).then(setWorlds).catch(() => setWorlds([]));
  }, [pickingWorld, worlds, worldId]);

  // Switching modes: a world portal drops its in-world pairing; back again it
  // gets a fresh link so "Place exit" works.
  const chooseMode = (world: boolean) => {
    if (world === pickingWorld) return;
    if (world) onUpdate({ portalWorldId: '', portalWorldName: undefined, portalLink: undefined });
    else onUpdate({ portalWorldId: undefined, portalWorldName: undefined, portalLink: newPortalLink() });
  };
  const q = query.trim().toLowerCase();
  const shown = (worlds || []).filter((w) => !q || w.name.toLowerCase().includes(q)).slice(0, 40);

  return (
    <div className="control-group">
      <label>Portal</label>
      <div className="npc-settings">
        <input
          type="text"
          placeholder={toWorld ? `Label (default: To ${primitive.portalWorldName || 'the world'})` : 'Label over the ring (e.g. To the rooftop)'}
          value={primitive.portalLabel || ''}
          maxLength={40}
          onChange={(e) => onUpdate({ portalLabel: e.target.value })}
          disabled={isLocked}
        />
        <div className="portal-mode" role="radiogroup" aria-label="Portal leads to">
          <button type="button" role="radio" aria-checked={!pickingWorld} className={`texture-preset-btn ${!pickingWorld ? 'active' : ''}`} disabled={isLocked} onClick={() => chooseMode(false)}>In this world</button>
          <button type="button" role="radio" aria-checked={pickingWorld} className={`texture-preset-btn ${pickingWorld ? 'active' : ''}`} disabled={isLocked} onClick={() => chooseMode(true)}>Another world</button>
        </div>

        {!pickingWorld ? (
          <>
            <span className="npc-note">{partner ? `Leads to: ${partner[1].name}` : 'No exit yet — place one to make it work'}</span>
            {partner ? (
              <button className="texture-preset-btn" onClick={() => useWorldStore.getState().setSelectedObjectId(partner[0])}>Select the other end</button>
            ) : selected && (
              <button className="texture-preset-btn" disabled={isLocked} onClick={() => startPlacePortalExit(selected)}>Place exit</button>
            )}
          </>
        ) : (
          <>
            {toWorld ? (
              <span className="npc-note">
                Leads to <strong>{primitive.portalWorldName || 'a world'}</strong>.{' '}
                <a href={`/vr/${primitive.portalWorldId}`} target="_blank" rel="noreferrer">Open it</a>
              </span>
            ) : (
              <span className="npc-note">Choose the world it leads to:</span>
            )}
            <input type="text" placeholder="Search worlds" value={query} onChange={(e) => setQuery(e.target.value)} disabled={isLocked} />
            <div className="portal-worlds" role="listbox" aria-label="Worlds">
              {worlds === null ? <span className="npc-note">Loading worlds…</span>
                : shown.length === 0 ? <span className="npc-note">No worlds found.</span>
                : shown.map((w) => (
                  <button
                    key={w.id}
                    type="button"
                    role="option"
                    aria-selected={primitive.portalWorldId === w.id}
                    className={`portal-world ${primitive.portalWorldId === w.id ? 'active' : ''}`}
                    disabled={isLocked}
                    onClick={() => onUpdate({ portalWorldId: w.id, portalWorldName: w.name })}
                  >
                    <span>{w.name}</span>
                    <em>{w.mine ? 'Yours' : 'Public'}</em>
                  </button>
                ))}
            </div>
            <span className="npc-note">
              Visitors arrive beside that world&apos;s portal leading back here, if it has one; otherwise at its spawn point. Only people who can open the world can travel there. Portals to other worlds work in Play mode, not while editing.
            </span>
          </>
        )}
      </div>
    </div>
  );
}

/** NPC panel: rename, change its move or how far it wanders, and build status. */
function NpcSettings({ primitive, onUpdate, isLocked }: { primitive: PrimitiveItem; onUpdate: (u: Partial<PrimitiveItem>) => void; isLocked: boolean }) {
  const selected = useWorldStore((s) => s.selectedObjectId);
  const { worldId } = useParams<{ worldId: string }>();
  const npc = primitive.npc as NpcConfig;
  const set = (patch: Partial<NpcConfig>) => onUpdate({ npc: { ...npc, ...patch }, ...(patch.name !== undefined ? { name: patch.name || 'NPC' } : {}) });
  const moves = !!npcAnim(npc.anim).moves;
  return (
    <div className="control-group">
      <label>NPC</label>
      <div className="npc-settings">
        <input type="text" placeholder="Name" value={npc.name} maxLength={24} disabled={isLocked} onChange={(e) => set({ name: e.target.value })} />
        <select value={npc.anim} disabled={isLocked} onChange={(e) => set({ anim: e.target.value })}>
          {[...new Set(NPC_ANIMS.map((a) => a.group))].map((g) => (
            <optgroup key={g} label={g}>
              {NPC_ANIMS.filter((a) => a.group === g).map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
            </optgroup>
          ))}
        </select>
        {moves && (
          <>
            <span className="npc-note">Wanders up to {npc.radius} m from this spot</span>
            <input type="range" min={1} max={12} step={1} value={npc.radius} disabled={isLocked} onChange={(e) => set({ radius: +e.target.value })} />
          </>
        )}
        <div className="npc-talk-section">
          <NpcTalkSettings talk={npc.talk} gender={npc.gender} name={npc.name} disabled={isLocked} onChange={(talk) => set({ talk })} />
        </div>
        {npc.status !== 'ready' && (
          <span className={`npc-note ${npc.status === 'failed' ? 'bad' : ''}`}>
            {npc.status === 'building' ? 'Building the character. It appears here when ready, usually a few minutes.'
              : `Build failed: ${npc.error || 'unknown error'}`}
          </span>
        )}
        {npc.status === 'failed' && npc.buildId && selected && worldId && (
          <button className="texture-preset-btn" onClick={() => { set({ status: 'building', error: undefined }); watchNpcBuild(worldId, selected, npc.buildId!); }}>
            Check again
          </button>
        )}
      </div>
    </div>
  );
}

const PrimitiveControls: React.FC<PrimitiveControlsProps> = ({
  primitive,
  transformMode,
  onTransformModeChange,
  onUpdate,
  onDelete,
  onDuplicate,
  onClose,
}) => {
  const { shape, position, rotation, scale, color, texturePreset, textureUrl, pbrMaps, textureScale, isLocked, collidable } = primitive;
  const [, setMatTick] = useState(0); // re-render when the material clipboard fills
  const { dragStyle, onPointerDown: onDragPointerDown } = useDraggable();
  const [showTextureUpload, setShowTextureUpload] = useState(false);
  const [showPBRBrowser, setShowPBRBrowser] = useState(false);
  const [pbrUploading, setPbrUploading] = useState(false);
  const [pbrProgress, setPbrProgress] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Block native DOM events so OrbitControls doesn't steal pointer/mouse/wheel
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const stop = (e: Event) => e.stopPropagation();
    const events = ['pointerdown', 'pointermove', 'pointerup', 'mousedown', 'mousemove', 'mouseup', 'wheel', 'touchstart', 'touchmove', 'touchend'];
    events.forEach((evt) => el.addEventListener(evt, stop, { capture: true }));
    return () => { events.forEach((evt) => el.removeEventListener(evt, stop, { capture: true })); };
  }, []);

  // Upload a single file (blob) to S3 and return its URL
  const uploadToS3 = useCallback(async (file: Blob, fileName: string, contentType: string): Promise<string> => {
    const key = `textures/${Date.now()}-${fileName}`;
    return putObject(key, file, contentType);
  }, []);

  // Handle PBR texture file selection (image or zip)
  const handleTextureFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = file.name.split('.').pop()?.toLowerCase();

    if (ext === 'zip') {
      // PBR zip — extract and upload each map
      setPbrUploading(true);
      setPbrProgress('Extracting zip...');
      try {
        const zip = await JSZip.loadAsync(file);

        // Look for PBR map files (flexible naming)
        // Matches the LAST segment after _ or - in filenames like:
        //   Metal049A_1K-JPG_Color.jpg → "color"
        //   Grass005_1K-JPG_NormalGL.jpg → "normalgl"
        const mapNames: Record<string, string[]> = {
          color: ['color', 'diffuse', 'albedo', 'basecolor', 'base_color', 'diff', 'col'],
          normal: ['normal', 'norm', 'nrm', 'normalgl', 'normaldx', 'normalogl'],
          roughness: ['roughness', 'rough', 'rgh'],
          ao: ['ao', 'ambientocclusion', 'ambient_occlusion', 'occlusion', 'occ'],
          metallic: ['metallic', 'metalness', 'metal'],
        };

        const imageExts = ['jpg', 'jpeg', 'png', 'webp', 'tga'];
        const files = Object.keys(zip.files).filter(name => {
          const fileExt = name.split('.').pop()?.toLowerCase();
          return fileExt && imageExts.includes(fileExt) && !name.startsWith('__MACOSX');
        });

        // Extract the last segment of the filename (after final _ or -)
        // e.g. "Metal049A_1K-JPG_Color" → "color"
        const getMapType = (name: string): string => {
          const baseName = name.split('/').pop()?.split('.')[0] || '';
          const segments = baseName.split(/[_-]/);
          return segments[segments.length - 1].toLowerCase();
        };

        const findMap = (keywords: string[]): string | undefined => {
          return files.find(name => {
            const suffix = getMapType(name);
            return keywords.some(kw => suffix === kw || suffix.includes(kw));
          });
        };

        const colorFile = findMap(mapNames.color);
        if (!colorFile) {
          setPbrProgress('');
          setPbrUploading(false);
          alert('No color/diffuse/albedo map found in zip. Expected file names like: color.jpg, diffuse.png, albedo.jpg');
          return;
        }

        const normalFile = findMap(mapNames.normal);
        const roughnessFile = findMap(mapNames.roughness);
        const aoFile = findMap(mapNames.ao);
        const metallicFile = findMap(mapNames.metallic);

        const foundMaps = [colorFile, normalFile, roughnessFile, aoFile, metallicFile].filter(Boolean);
        setPbrProgress(`Found ${foundMaps.length} maps, uploading...`);

        const uploadMap = async (fileName: string): Promise<string> => {
          const data = await zip.files[fileName].async('blob');
          const mapExt = fileName.split('.').pop()?.toLowerCase() || 'jpg';
          const ct = mapExt === 'png' ? 'image/png' : 'image/jpeg';
          return uploadToS3(data, fileName.split('/').pop() || fileName, ct);
        };

        const result: PbrMaps = { colorUrl: '' };

        setPbrProgress('Uploading color map...');
        result.colorUrl = await uploadMap(colorFile);

        if (normalFile) {
          setPbrProgress('Uploading normal map...');
          result.normalUrl = await uploadMap(normalFile);
        }
        if (roughnessFile) {
          setPbrProgress('Uploading roughness map...');
          result.roughnessUrl = await uploadMap(roughnessFile);
        }
        if (aoFile) {
          setPbrProgress('Uploading AO map...');
          result.aoUrl = await uploadMap(aoFile);
        }
        if (metallicFile) {
          setPbrProgress('Uploading metallic map...');
          result.metallicUrl = await uploadMap(metallicFile);
        }

        onUpdate({ pbrMaps: result, textureUrl: null, texturePreset: null });
        setPbrProgress('');
        console.log('[PBR] Uploaded maps:', result);
      } catch (err) {
        console.error('[PBR] Zip upload error:', err);
        alert('Failed to process PBR zip: ' + (err instanceof Error ? err.message : 'Unknown error'));
      } finally {
        setPbrUploading(false);
        setPbrProgress('');
      }
    } else if (ext && ['jpg', 'jpeg', 'png'].includes(ext)) {
      // Single image texture
      setPbrUploading(true);
      setPbrProgress('Uploading texture...');
      try {
        const ct = ext === 'png' ? 'image/png' : 'image/jpeg';
        const url = await uploadToS3(file, file.name, ct);
        onUpdate({ textureUrl: url, pbrMaps: null, texturePreset: null });
      } catch (err) {
        console.error('[Texture] Upload error:', err);
        alert('Failed to upload texture: ' + (err instanceof Error ? err.message : 'Unknown error'));
      } finally {
        setPbrUploading(false);
        setPbrProgress('');
      }
    } else {
      alert('Unsupported file type. Use .jpg, .png, or .zip (PBR pack)');
    }

    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [onUpdate, uploadToS3]);

  return (
    <>
    <div ref={panelRef} className="primitive-controls glassmorphism-dark" style={dragStyle} onPointerDown={(e) => { e.stopPropagation(); onDragPointerDown(e); }}>
      <div className="controls-header" data-drag-handle>
        <h3>{SHAPE_LABELS[shape]} Properties</h3>
        <div className="header-buttons">
          <button
            className={`icon-btn ${isLocked ? 'locked' : ''}`}
            onClick={() => onUpdate({ isLocked: !isLocked })}
            title={isLocked ? 'Unlock' : 'Lock'}
          >
            {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
          </button>
          <button className="icon-btn" onClick={onClose} title="Close"><X size={16} /></button>
        </div>
      </div>

      {/* Transform mode toggle */}
      <div className="transform-mode-toggle">
        {([
          { mode: 'translate' as const, icon: <Move size={14} />, label: 'Move' },
          { mode: 'rotate' as const, icon: <RotateCw size={14} />, label: 'Rotate' },
          { mode: 'scale' as const, icon: <Maximize2 size={14} />, label: 'Scale' },
        ]).map(({ mode, icon, label }) => (
          <button
            key={mode}
            className={`transform-mode-btn ${transformMode === mode ? 'active' : ''}`}
            onClick={() => onTransformModeChange(mode)}
            title={label}
            disabled={isLocked}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className={`controls-content ${isLocked ? 'disabled' : ''}`}>
        {/* Position */}
        <div className="control-group">
          <label>Position</label>
          {(['X', 'Y', 'Z'] as const).map((axis, i) => (
            <div className="control-row" key={axis}>
              <span>{axis}:</span>
              <input
                type="range" min={-50} max={50} step={0.1}
                value={position[i]}
                onChange={(e) => {
                  const newPos: [number, number, number] = [...position];
                  newPos[i] = parseFloat(e.target.value);
                  onUpdate({ position: newPos });
                }}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={parseFloat(position[i].toFixed(1))}
                onChange={(e) => {
                  const newPos: [number, number, number] = [...position];
                  newPos[i] = parseFloat(e.target.value) || 0;
                  onUpdate({ position: newPos });
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.1}
                disabled={isLocked}
              />
            </div>
          ))}
        </div>

        {/* Scale */}
        <div className="control-group">
          <label>Scale</label>
          {(['X', 'Y', 'Z'] as const).map((axis, i) => (
            <div className="control-row" key={axis}>
              <span>{axis}:</span>
              <input
                type="range" min={0.01} max={20} step={0.01}
                value={scale[i]}
                onChange={(e) => {
                  const newScale: [number, number, number] = [...scale];
                  newScale[i] = parseFloat(e.target.value);
                  onUpdate({ scale: newScale });
                }}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={parseFloat(scale[i].toFixed(2))}
                onChange={(e) => {
                  const newScale: [number, number, number] = [...scale];
                  newScale[i] = parseFloat(e.target.value) || 0.01;
                  onUpdate({ scale: newScale });
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.01}
                min={0.01}
                disabled={isLocked}
              />
            </div>
          ))}
        </div>

        {/* Rotation */}
        <div className="control-group">
          <label>Rotation</label>
          {(['X', 'Y', 'Z'] as const).map((axis, i) => {
            const deg = Math.round(rotation[i] * (180 / Math.PI));
            return (
              <div className="control-row" key={axis}>
                <span>{axis}:</span>
                <input
                  type="range" min={-180} max={180} step={1}
                  value={deg}
                  onChange={(e) => {
                    const newRot: [number, number, number] = [...rotation];
                    newRot[i] = parseFloat(e.target.value) * (Math.PI / 180);
                    onUpdate({ rotation: newRot });
                  }}
                  disabled={isLocked}
                />
                <input
                  type="number"
                  className="value-input"
                  value={deg}
                  onChange={(e) => {
                    const newRot: [number, number, number] = [...rotation];
                    newRot[i] = (parseFloat(e.target.value) || 0) * (Math.PI / 180);
                    onUpdate({ rotation: newRot });
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  step={1}
                  disabled={isLocked}
                />
                <span style={{ color: '#888', fontSize: 11 }}>&deg;</span>
              </div>
            );
          })}
        </div>

        {/* Game table: per-part colours instead of one colour / material */}

        {/* Color */}
        {shape === 'arcade' && <ArcadeSettings primitive={primitive} onUpdate={onUpdate} isLocked={isLocked} />}
        {shape === 'leaderboard' && <LeaderboardSettings primitive={primitive} onUpdate={onUpdate} isLocked={isLocked} />}

        {shape !== 'gametable' && shape !== 'arcade' && shape !== 'leaderboard' && shape !== 'npc' && shape !== 'worldgame' && <div className="control-group">
          <label>Color</label>
          <div className="control-row">
            <span>Color:</span>
            <input
              type="color"
              value={color}
              onChange={(e) => onUpdate({ color: e.target.value })}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              disabled={isLocked}
              className="color-input"
              style={{ width: 36, height: 28, cursor: 'pointer', border: 'none', padding: 0, background: 'transparent' }}
            />
            <span className="value">{color}</span>
          </div>
        </div>}

        {/* Portal: where it leads */}
        {shape === 'portal' && <PortalSettings primitive={primitive} onUpdate={onUpdate} isLocked={isLocked} />}

        {/* NPC: name, move, status */}
        {shape === 'npc' && primitive.npc && <NpcSettings primitive={primitive} onUpdate={onUpdate} isLocked={isLocked} />}

        {/* Speaker: tracks, volume, range, loop (components/music) */}
        {shape === 'speaker' && <SpeakerSection primitive={primitive} onUpdate={onUpdate} isLocked={isLocked} />}

        {/* World game piece: game, role settings (components/games/worldgame) */}
        {shape === 'worldgame' && <React.Suspense fallback={null}><WorldGameSettings primitive={primitive} onUpdate={onUpdate} isLocked={isLocked} /></React.Suspense>}

        {/* Glass / Material */}
        {shape !== 'text' && shape !== 'portal' && shape !== 'gametable' && shape !== 'arcade' && shape !== 'leaderboard' && shape !== 'npc' && shape !== 'speaker' && shape !== 'worldgame' && (
          <div className="control-group">
            <label>Glass / Material</label>
            <div className="control-row">
              <span>Opacity:</span>
              <input
                type="range" min={0} max={1} step={0.05}
                value={primitive.opacity ?? 1}
                onChange={(e) => onUpdate({ opacity: parseFloat(e.target.value) })}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={parseFloat((primitive.opacity ?? 1).toFixed(2))}
                onChange={(e) => onUpdate({ opacity: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.05}
                min={0}
                max={1}
                disabled={isLocked}
              />
            </div>
            <div className="control-row">
              <span>Metal:</span>
              <input
                type="range" min={0} max={1} step={0.05}
                value={primitive.metalness ?? 0}
                onChange={(e) => onUpdate({ metalness: parseFloat(e.target.value) })}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={parseFloat((primitive.metalness ?? 0).toFixed(2))}
                onChange={(e) => onUpdate({ metalness: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.05}
                min={0}
                max={1}
                disabled={isLocked}
              />
            </div>
            <div className="control-row">
              <span title="Neon glow — the shape emits its own color. Turn on Bloom in Atmosphere for the full halo.">Glow:</span>
              <input
                type="range" min={0} max={5} step={0.1}
                value={primitive.emissive ?? 0}
                onChange={(e) => onUpdate({ emissive: parseFloat(e.target.value) })}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={parseFloat((primitive.emissive ?? 0).toFixed(1))}
                onChange={(e) => onUpdate({ emissive: Math.min(5, Math.max(0, parseFloat(e.target.value) || 0)) })}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.1}
                min={0}
                max={5}
                disabled={isLocked}
              />
            </div>
            <div className="control-row">
              <span>Rough:</span>
              <input
                type="range" min={0} max={1} step={0.05}
                value={primitive.roughness ?? 0.5}
                onChange={(e) => onUpdate({ roughness: parseFloat(e.target.value) })}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={parseFloat((primitive.roughness ?? 0.5).toFixed(2))}
                onChange={(e) => onUpdate({ roughness: Math.min(1, Math.max(0, parseFloat(e.target.value) || 0)) })}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.05}
                min={0}
                max={1}
                disabled={isLocked}
              />
            </div>
          </div>
        )}

        {/* Text Content (only for text shapes) */}
        {shape === 'text' && (
          <div className="control-group">
            <label>Text Content</label>
            <div className="control-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 6 }}>
              <textarea
                value={primitive.text || ''}
                onChange={(e) => onUpdate({ text: e.target.value })}
                disabled={isLocked}
                placeholder="Enter text..."
                rows={3}
                style={{
                  width: '100%',
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#fff',
                  padding: '8px 10px',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                }}
              />
              <div className="control-row">
                <span>Size:</span>
                <input
                  type="range" min={0.1} max={5} step={0.1}
                  value={primitive.fontSize || 1}
                  onChange={(e) => onUpdate({ fontSize: parseFloat(e.target.value) })}
                  disabled={isLocked}
                />
                <input
                  type="number"
                  className="value-input"
                  value={parseFloat((primitive.fontSize || 1).toFixed(1))}
                  onChange={(e) => onUpdate({ fontSize: parseFloat(e.target.value) || 0.1 })}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  step={0.1}
                  min={0.1}
                  disabled={isLocked}
                />
              </div>
            </div>
          </div>
        )}

        {shape !== 'portal' && shape !== 'npc' && shape !== 'arcade' && shape !== 'leaderboard' && shape !== 'worldgame' && <>
        {/* Collision */}
        <div className="control-group">
          <label>Physics</label>
          <div className="control-row">
            <span><ShieldCheck size={14} /></span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={collidable}
                onChange={() => onUpdate({ collidable: !collidable })}
                disabled={isLocked}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="value" style={{ fontSize: 11 }}>{collidable ? 'Solid' : 'Walk-through'}</span>
          </div>
          <div className="control-row">
            <span><Box size={14} /></span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={primitive.colliderOnly ?? false}
                onChange={() => onUpdate({ colliderOnly: !(primitive.colliderOnly ?? false), collidable: true })}
                disabled={isLocked}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="value" style={{ fontSize: 11 }}>{primitive.colliderOnly ? 'Collider Only (invisible)' : 'Visible mesh'}</span>
          </div>
          <div className="control-row">
            <span><Armchair size={14} /></span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={primitive.seat ?? false}
                onChange={() => onUpdate({ seat: !(primitive.seat ?? false), ...(primitive.seat ? {} : { collidable: true }) })}
                disabled={isLocked}
              />
              <span className="toggle-slider"></span>
            </label>
            <span className="value" style={{ fontSize: 11 }}>{primitive.seat ? 'Seat (click to sit)' : 'Not a seat'}</span>
          </div>
        </div>

        </>}

        {/* Material (PBR) — hidden for text objects */}
        {shape !== 'text' && shape !== 'portal' && shape !== 'gametable' && shape !== 'arcade' && shape !== 'leaderboard' && shape !== 'npc' && shape !== 'speaker' && shape !== 'worldgame' && <div className="control-group">
          <label>Material (PBR)</label>
          <div className="texture-preset-grid">
            <button
              className={`texture-preset-btn ${!texturePreset && !textureUrl && !pbrMaps ? 'active' : ''}`}
              onClick={() => onUpdate({ texturePreset: null, textureUrl: null, pbrMaps: null })}
              disabled={isLocked}
              title="No texture"
            >
              <div className="preset-swatch" style={{ background: color }} />
              <span>None</span>
            </button>
            {TEXTURE_PRESETS.map((p) => (
              <button
                key={p.id}
                className={`texture-preset-btn ${texturePreset === p.id ? 'active' : ''}`}
                onClick={() => onUpdate({ texturePreset: p.id, textureUrl: null, pbrMaps: null })}
                disabled={isLocked}
                title={p.label}
              >
                <img src={`/textures/${p.id}/color.jpg`} alt={p.label} className="preset-thumb" />
                <span>{p.label}</span>
              </button>
            ))}
          </div>

          {/* Custom Texture / PBR Upload */}
          <div style={{ marginTop: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.zip"
              onChange={handleTextureFile}
              style={{ display: 'none' }}
            />

            {pbrUploading && (
              <div className="pbr-upload-progress" style={{ marginBottom: 6 }}>
                <Loader size={14} className="s3-spinner" />
                <span style={{ fontSize: 11, marginLeft: 6 }}>{pbrProgress}</span>
              </div>
            )}

            {!pbrUploading && pbrMaps && (
              <div className="custom-texture-preview" style={{ marginBottom: 6 }}>
                <img src={pbrMaps.colorUrl} alt="PBR color" className="preset-thumb" style={{ width: 40, height: 40, borderRadius: 4 }} />
                <div style={{ flex: 1, overflow: 'hidden', fontSize: 11 }}>
                  <div style={{ fontWeight: 600 }}>PBR Material</div>
                  <div style={{ opacity: 0.7 }}>
                    {[
                      'Color',
                      pbrMaps.normalUrl && 'Normal',
                      pbrMaps.roughnessUrl && 'Rough',
                      pbrMaps.aoUrl && 'AO',
                      pbrMaps.metallicUrl && 'Metal',
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button
                  className="icon-btn"
                  onClick={() => onUpdate({ pbrMaps: null })}
                  disabled={isLocked}
                  title="Remove PBR material"
                  style={{ marginLeft: 4 }}
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {!pbrUploading && !pbrMaps && textureUrl && (
              <div className="custom-texture-preview" style={{ marginBottom: 6 }}>
                <img src={textureUrl} crossOrigin="anonymous" alt="Custom texture" className="preset-thumb" style={{ width: 40, height: 40, borderRadius: 4 }} />
                <span style={{ fontSize: 11, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Custom texture</span>
                <button
                  className="icon-btn"
                  onClick={() => onUpdate({ textureUrl: null })}
                  disabled={isLocked}
                  title="Remove custom texture"
                  style={{ marginLeft: 4 }}
                >
                  <X size={12} />
                </button>
              </div>
            )}

            {!pbrUploading && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <button
                  className="texture-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isLocked}
                  style={{ flex: 1, minWidth: 80 }}
                >
                  <Upload size={14} /> Upload
                </button>
                <button
                  className="texture-upload-btn"
                  onClick={() => setShowPBRBrowser(true)}
                  disabled={isLocked}
                  title="Browse free PBR materials from ambientCG"
                  style={{ flex: 1, minWidth: 80 }}
                >
                  <BookOpen size={14} /> Library
                </button>
              </div>
            )}
          </div>
        </div>}

        {/* Texture Scale */}
        {(texturePreset || textureUrl || pbrMaps) && (
          <div className="control-group">
            <label>Pattern Scale</label>
            <div className="control-row">
              <span>Tile X:</span>
              <input
                type="range" min={0.5} max={10} step={0.25}
                value={textureScale}
                onChange={(e) => onUpdate({ textureScale: parseFloat(e.target.value) })}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={textureScale}
                onChange={(e) => onUpdate({ textureScale: parseFloat(e.target.value) || 0.5 })}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.25}
                min={0.5}
                disabled={isLocked}
              />
            </div>
            <div className="control-row">
              <span>Tile Y:</span>
              <input
                type="range" min={0.5} max={10} step={0.25}
                value={primitive.textureScaleY ?? textureScale}
                onChange={(e) => onUpdate({ textureScaleY: parseFloat(e.target.value) })}
                disabled={isLocked}
              />
              <input
                type="number"
                className="value-input"
                value={primitive.textureScaleY ?? textureScale}
                onChange={(e) => onUpdate({ textureScaleY: parseFloat(e.target.value) || 0.5 })}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                step={0.25}
                min={0.5}
                disabled={isLocked}
              />
            </div>
            {primitive.textureScaleY != null && primitive.textureScaleY !== textureScale && (
              <button className="reset-btn" style={{ marginTop: 6 }} disabled={isLocked}
                onClick={() => onUpdate({ textureScaleY: undefined })}>
                Link X/Y tiling
              </button>
            )}
          </div>
        )}

        {/* Material transfer: copy this object's whole look, apply to another */}
        {shape !== 'npc' && shape !== 'arcade' && shape !== 'leaderboard' && shape !== 'speaker' && shape !== 'worldgame' && <div className="control-group">
          <label>Material Transfer</label>
          <div className="control-row" style={{ gap: 8 }}>
            <button
              className="reset-btn"
              disabled={isLocked}
              onClick={() => {
                materialClipboard = {
                  color, texturePreset, textureUrl, pbrMaps, textureScale,
                  textureScaleY: primitive.textureScaleY,
                  metalness: primitive.metalness, roughness: primitive.roughness,
                  opacity: primitive.opacity, emissive: primitive.emissive ?? 0,
                };
                setMatTick((t) => t + 1);
              }}
            >
              Copy Material
            </button>
            <button
              className="reset-btn"
              disabled={isLocked || !materialClipboard}
              onClick={() => materialClipboard && onUpdate({ ...materialClipboard })}
            >
              Apply Material
            </button>
          </div>
        </div>}

        {/* Actions */}
        <div className="primitive-actions">
          <button className="action-btn duplicate-btn" onClick={onDuplicate} disabled={isLocked}>
            <Copy size={14} /> Duplicate
          </button>
          <button className="action-btn delete-btn" onClick={onDelete} disabled={isLocked}>
            <Trash2 size={14} /> Delete
          </button>
        </div>

        <button className="reset-btn" onClick={() => onUpdate({ position: [0, 0.5, 0], rotation: [0, 0, 0], scale: [1, 1, 1], color: '#888888', texturePreset: null, textureUrl: null, pbrMaps: null, textureScale: 2, opacity: 1, metalness: 0, roughness: 0.5 })} disabled={isLocked}>
          <RotateCcw size={16} /> Reset
        </button>
      </div>

    </div>
    {showPBRBrowser && createPortal(
      <PBRBrowser
        onApply={(maps) => {
          onUpdate({ pbrMaps: maps, textureUrl: null, texturePreset: null });
          setShowPBRBrowser(false);
        }}
        onClose={() => setShowPBRBrowser(false)}
      />,
      document.body
    )}
    </>
  );
};

export default PrimitiveControls;

/** Arcade cabinet: which game it plays (switching keeps its place in the world). */
function ArcadeSettings({ primitive, onUpdate, isLocked }: { primitive: PrimitiveItem; onUpdate: (u: Partial<PrimitiveItem>) => void; isLocked: boolean }) {
  const { games, ready } = useArcadeGames();
  const cur = games.find((g) => g.id === primitive.arcadeGame);
  return (
    <div className="control-group">
      <label>Arcade game</label>
      {cur && <img src={artDataUrl(cur.artSvg)} alt="" style={{ width: '100%', aspectRatio: '400 / 240', borderRadius: 10, display: 'block', marginBottom: 8 }} />}
      <div className="control-row">
        <select
          value={primitive.arcadeGame || ''}
          disabled={isLocked || !ready}
          onChange={(e) => { const g = games.find((x) => x.id === e.target.value); if (g) onUpdate({ arcadeGame: g.id, name: `${g.title} Cabinet` }); }}
          onMouseDown={(e) => e.stopPropagation()}
          style={{ flex: 1 }}
        >
          {!cur && <option value={primitive.arcadeGame || ''}>{ready ? `Unknown (${primitive.arcadeGame || '?'})` : 'Loading…'}</option>}
          {games.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
        </select>
      </div>
    </div>
  );
}

/** Arcade scoreboard: which game, period, scope, title, look, rows, size and mount. */
function LeaderboardSettings({ primitive, onUpdate, isLocked }: { primitive: PrimitiveItem; onUpdate: (u: Partial<PrimitiveItem>) => void; isLocked: boolean }) {
  const { games } = useArcadeGames();
  const cfg = boardConfig(primitive);
  const [title, setTitle] = useState(cfg.title);
  useEffect(() => { setTitle(cfg.title); }, [cfg.title]);
  const size = +(primitive.scale?.[0] ?? 1).toFixed(2);
  const set = (patch: Partial<LeaderboardConfig>) => {
    const next = { ...cfg, ...patch };
    const y = reframeBoardY(primitive.position[1], primitive.scale[1], cfg, next);
    onUpdate({ board: next, ...(y !== primitive.position[1] ? { position: [primitive.position[0], y, primitive.position[2]] as [number, number, number] } : {}) });
  };
  const setSize = (k: number) => {
    const y = reframeBoardY(primitive.position[1], primitive.scale[1], cfg, cfg, k);
    onUpdate({ scale: [k, k, k], position: [primitive.position[0], y, primitive.position[2]] });
  };
  const seg = <T extends string | number>(value: T, options: [T, string][], on: (v: T) => void) => (
    <div className="lb-seg" role="radiogroup">
      {options.map(([v, label]) => (
        <button key={String(v)} type="button" role="radio" aria-checked={value === v} className={value === v ? 'active' : ''} disabled={isLocked}
          onClick={() => on(v)} onMouseDown={(e) => e.stopPropagation()}>{label}</button>
      ))}
    </div>
  );
  return (
    <div className="control-group lb-settings">
      <label>Leaderboard</label>
      <div className="lb-field"><span>Game</span>
        <select value={cfg.game} disabled={isLocked} onChange={(e) => set({ game: e.target.value })} onMouseDown={(e) => e.stopPropagation()}>
          <option value="all">All games (rotate)</option>
          {games.map((g) => <option key={g.id} value={g.id}>{g.title}</option>)}
          {WORLD_GAME_BOARDS.map((b) => <option key={b.id} value={b.id}>{b.title} ({b.units === 'count' ? `most ${b.unitLabel ?? 'points'}` : 'fastest times'})</option>)}
        </select>
      </div>
      <div className="lb-field"><span>Period</span>{seg(cfg.period, [['all', 'All time'], ['week', 'This week'], ['today', 'Today']], (period) => set({ period }))}</div>
      <div className="lb-field"><span>Scores from</span>{seg(cfg.scope, [['world', 'This world'], ['global', 'All worlds']], (scope) => set({ scope }))}</div>
      <div className="lb-field"><span>Title</span>
        <input type="text" value={title} maxLength={24} disabled={isLocked} placeholder="HIGH SCORES"
          onChange={(e) => setTitle(e.target.value)} onBlur={() => { if (title.trim() !== cfg.title) set({ title: title.trim() || 'HIGH SCORES' }); }}
          onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} onMouseDown={(e) => e.stopPropagation()} />
      </div>
      <div className="lb-field"><span>Look</span>
        <div className="lb-themes">
          {BOARD_THEMES.map((t) => (
            <button key={t.id} type="button" title={t.label} aria-label={t.label} disabled={isLocked}
              className={`lb-theme${cfg.theme === t.id ? ' active' : ''}${t.id === 'auto' ? ' lb-theme--auto' : ''}`}
              style={{ background: t.id === 'auto' ? undefined : `linear-gradient(135deg, ${t.c1}, ${t.c2})` }}
              onClick={() => set({ theme: t.id })} onMouseDown={(e) => e.stopPropagation()} />
          ))}
        </div>
      </div>
      <div className="lb-field"><span>Rows</span>{seg(cfg.rows, [[5, 'Top 5'], [10, 'Top 10']], (rows) => set({ rows }))}</div>
      <div className="lb-field"><span>Mount</span>{seg(cfg.mount, [['stand', 'Freestanding'], ['wall', 'On a wall']], (mount) => set({ mount }))}</div>
      <div className="lb-field"><span>Size</span>
        <input type="range" min={0.5} max={2.5} step={0.05} value={size} disabled={isLocked}
          onChange={(e) => setSize(parseFloat(e.target.value))} onMouseDown={(e) => e.stopPropagation()} />
        <span className="value">{size.toFixed(2)}×</span>
      </div>
    </div>
  );
}
